export default function EventCollection(
  Authentication,
  EventProcessorService,
  HttpLayer,
  PersonaCollection,
  Socket,
  Utils,
  sdk,
  $rootScope
) {
  'ngInject';

  const CalendarCache = sdk('CalendarCache');
  const calendarContext = sdk('context');
  const { LEAVE_TYPE } = sdk('constants');

  const service = this;

  const calendarCache = new CalendarCache({
    ...HttpLayer,
    syncWorker: _updateEventProcessorWorker,
  });

  const writableProperties = [
    'allEvents',
    'allUsers',
    'notes',
    'lastSeenDate',
    'timeOffTypes',
    'undoingPossible',
    'redoTransactionId',
    'staleLaborData',
  ];

  [
    'notesDict',
    'range',
    'currentShifts',
    'shiftTemplates',
    'leaveSuppressions',
    'businessClosedEvents',
    'holidayEvents',
    'holidayRateEvents',
    'salaryCosts',
    ...writableProperties,
  ].forEach((property) => {
    Object.defineProperty(service, property, {
      get() {
        return calendarCache[property];
      },
      set(value) {
        if (writableProperties.includes(property)) {
          calendarCache[property] = value;
        }
      },
    });
  });

  service.httpGet = calendarCache.httpGet;
  service.httpPost = calendarCache.httpPost;
  service.httpPut = calendarCache.httpPut;
  service.httpDelete = calendarCache.httpDelete;

  service.syncEvents = calendarCache.syncEvents;
  service.syncLabor = calendarCache.syncLabor;
  service.clearData = calendarCache.clearData;
  service.getEvent = calendarCache.getEvent;
  service.getTimesheetEvent = calendarCache.getTimesheetEvent;
  service.getEvents = calendarCache.getEvents;
  service.getShiftsCosts = calendarCache.getShiftsCosts;
  service.onChange = calendarCache.onChange;
  service.addOrEditEvents = calendarCache.addOrEditEvents;
  service.deleteEvent = calendarCache.deleteEvent;
  service.addOrEditNote = calendarCache.addOrEditNote;
  service.deleteNote = calendarCache.deleteNote;
  service.getLogs = calendarCache.getLogs;

  service.addShift = calendarCache.addShift;
  service.updateShift = calendarCache.updateShift;
  service.shiftsBulk = calendarCache.shiftsBulk;
  service.addTimeoff = calendarCache.addTimeoff;
  service.updateTimeoff = calendarCache.updateTimeoff;
  service.applyForShift = calendarCache.applyForShift;
  service.deleteShift = calendarCache.deleteShift;
  service.deleteShifts = calendarCache.deleteShifts;
  service.publishShift = calendarCache.publishShift;
  service.publishShifts = calendarCache.publishShifts;
  service.offerShift = calendarCache.offerShift;
  service.cancelShiftAvailability = calendarCache.cancelShiftAvailability;
  service.getShiftCoworkers = calendarCache.getShiftCoworkers;
  service.getShiftDetails = calendarCache.getShiftDetails;
  service.makeShiftAvailable = calendarCache.makeShiftAvailable;
  service.makeShiftsAvailable = calendarCache.makeShiftsAvailable;
  service.acceptShiftOffer = calendarCache.acceptShiftOffer;
  service.rejectShiftOffer = calendarCache.rejectShiftOffer;
  service.approveShiftOffer = calendarCache.approveShiftOffer;
  service.retractShiftOffer = calendarCache.retractShiftOffer;
  service.cancelShiftOffer = calendarCache.cancelShiftOffer;
  service.denyShiftApplicant = calendarCache.denyShiftApplicant;
  service.denyShiftApplicants = calendarCache.denyShiftApplicants;
  service.retractShiftApplication = calendarCache.retractShiftApplication;
  service.approveShiftApplicant = calendarCache.approveShiftApplicant;
  service.approveShiftApplications = calendarCache.approveShiftApplications;
  service.copyShifts = calendarCache.copyShifts;
  service.checkDirty = calendarCache.checkDirty;
  service.checkUndoRedo = calendarCache.checkUndoRedo;
  service.undoChanges = calendarCache.undoChanges;
  service.redoChanges = calendarCache.redoChanges;
  service.getNextShifts = calendarCache.getNextShifts;
  service.getCurrentShifts = calendarCache.getCurrentShifts;
  service.getClockIn = calendarCache.getClockIn;
  service.clockIn = calendarCache.clockIn;
  service.clockOut = calendarCache.clockOut;
  service.takeBreak = calendarCache.takeBreak;
  service.endBreak = calendarCache.endBreak;
  service.updateCurrentShifts = calendarCache.updateCurrentShifts;
  service.onCurrentShiftsChange = calendarCache.onCurrentShiftsChange;
  service.getTimeOffTypes = calendarCache.getTimeOffTypes;
  service.saveTimeOffTypes = calendarCache.saveTimeOffTypes;
  service.getUserLeavesStatus = calendarCache.getUserLeavesStatus;
  service.getUserCaps = calendarCache.getUserCaps;
  service.updateUserCaps = calendarCache.updateUserCaps;
  service.syncUserLeaveCaps = calendarCache.syncUserLeaveCaps;
  service.getNoShowSummary = calendarCache.getNoShowSummary;
  service.getSickCalloutSummary = calendarCache.getSickCalloutSummary;
  service.addNoShow = calendarCache.addNoShow;
  service.addSickCallout = calendarCache.addSickCallout;
  service.deleteNoShow = calendarCache.deleteNoShow;
  service.deleteSickCallout = calendarCache.deleteSickCallout;
  service.markDay = calendarCache.markDay;
  service.unmarkDay = calendarCache.unmarkDay;
  service.markDayBulk = calendarCache.markDayBulk;
  service.getLastClockIn = calendarCache.getLastClockIn;
  service.loadShiftsAvailableForSwap = calendarCache.loadShiftsAvailableForSwap;
  service.initiateSwap = calendarCache.initiateSwap;
  service.loadSwapDetails = calendarCache.loadSwapDetails;
  service.cancelShiftSwap = calendarCache.cancelShiftSwap;
  service.approveShiftSwap = calendarCache.approveShiftSwap;
  service._notifyOnChange = calendarCache._notifyOnChange;
  service.updateCalendarSocketRoom = calendarCache.updateCalendarSocketRoom;

  service.addShiftTemplate = calendarCache.addShiftTemplate;
  service.getShiftTemplates = calendarCache.getShiftTemplates;
  service.updateShiftTemplate = calendarCache.updateShiftTemplate;
  service.deleteShiftTemplate = calendarCache.deleteShiftTemplate;
  service.syncShiftTemplates = calendarCache.syncShiftTemplates;
  service.acceptShift = calendarCache.acceptShift;
  service.rejectShift = calendarCache.rejectShift;
  service.bulkAcceptShifts = calendarCache.bulkAcceptShifts;
  service.approveTimeOff = calendarCache.approveTimeOff;
  service.denyTimeOff = calendarCache.denyTimeOff;
  service.deleteTimeOff = calendarCache.deleteTimeOff;

  service.isTimeOff = function isTimeOff(eventType) {
    return eventType === LEAVE_TYPE;
  };

  PersonaCollection.onChange(() => {
    calendarCache._mergeUsers();
    calendarCache._notifyOnChange();
    calendarCache.syncShiftTemplates();
    PersonaCollection.updateVisibleUsers();
  });

  Authentication.onOrgUpdate((org, oldOrg) => {
    if (org.subscriptionLevel !== oldOrg.subscriptionLevel) {
      calendarCache.syncShiftTemplates();
    }
  });

  // SOCKET UPDATES

  $rootScope.$on('socket:event:new', (event, data) => {
    !__TEST__ && console.log('event:new');
    service.addOrEditEvents([data]);
  });

  $rootScope.$on('socket:event:edit', (event, data) => {
    !__TEST__ && console.log('event:edit');
    service.addOrEditEvents([data]);
  });

  $rootScope.$on('socket:event:delete', (event, data) => {
    !__TEST__ && console.log('event:delete');
    service.deleteEvent(data);
  });

  $rootScope.$on('socket:note:new', (event, data) => {
    !__TEST__ && console.log('note:new');
    service.addOrEditNote(data);
  });

  $rootScope.$on('socket:note:edit', (event, data) => {
    !__TEST__ && console.log('note:edit');
    service.addOrEditNote(data);
  });

  $rootScope.$on('socket:note:delete', (event, data) => {
    !__TEST__ && console.log('note:delete');
    service.deleteNote(data);
  });

  $rootScope.$on('socket:events:sync', () => {
    !__TEST__ && console.log('events:sync');
    service.syncEvents();
  });

  $rootScope.$on('socket:labor:inprogress', () => {
    !__TEST__ && console.log('labor:inprogress');
    service.staleLaborData = true;
  });

  $rootScope.$on('socket:labor:changed', (event, data) => {
    !__TEST__ && console.log('labor:changed');
    service.staleLaborData = data.stale;
    service.syncLabor();
  });

  $rootScope.$on('socket:shifts:current:sync', () => {
    !__TEST__ && console.log('shifts:current:sync');
    service.updateCurrentShifts(null);
  });

  $rootScope.$on('socket:shifts:import:complete', (event, data) => {
    !__TEST__ && console.log({ name: 'shifts:import:complete', event, data });
    service.syncEvents();
  });

  $rootScope.$on('socket:shift:templates:sync', () => {
    !__TEST__ && console.log('shift:templates:sync');
    service.syncShiftTemplates();
  });

  // extend context
  Object.assign(calendarContext, {
    isTimeOff: (eventType) => service.isTimeOff(eventType),
    getTimeOffTypes: () => calendarCache.timeOffTypes,
    getDecimalFormat: () => Utils.getDecimalFormat(),
  });

  function _updateEventProcessorWorker(args) {
    return EventProcessorService.updateEvents(service, args);
  }
}
